import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { Graduation, PersonalSkill, RunOutcome, VettingStatus } from '../../types/aai';
import {
  Action, Chip, Divided, Drawer, EmptyState, Kicker, ListRow, Loading, Panel, PanelHead, RouteMark,
} from '../../components/aa';
import { relativeTime } from '../partner/format';
import { SkillBuilderDrawer } from './SkillBuilder';
import { SkillRunBody } from './SkillRun';

/*
 * Skills you built, and offering one to your team.
 *
 * A skill starts personal. You use it on real work and record how each run
 * went; once it has worked enough times, offering it to your colleagues is
 * yours to offer — nobody shares your work for you. That offer had no home on
 * this portal: it existed only on the old tools page, so the person who did
 * the work could not take the step the program asks of them.
 *
 * The builder itself opens inside a panel here rather than on a page of its
 * own, the same way a training step opens the surface it is anchored to.
 */

const SHARE_STATUS: Record<string, { label: string; tone: 'warn' | 'ok' | 'err' }> = {
  proposed: { label: 'Offered to your team', tone: 'warn' },
  approved: { label: 'Shared with your team', tone: 'ok' },
  declined: { label: 'Not shared', tone: 'err' },
};

const progressLine = (v: VettingStatus | null, grad: Graduation | null): string => {
  if (grad && grad.status === 'proposed') return 'Waiting on the program team to review the share.';
  if (grad && grad.status === 'approved') return 'Your colleagues can use this one now.';
  if (grad && grad.status === 'declined') return grad.note || 'Revise it and offer it again when it is ready.';
  if (!v) return 'Run record loading.';
  if (v.vetted) return `Worked ${v.worked} of ${v.runs} times. Ready to offer to your team.`;
  return `Worked ${v.worked} of ${v.runs} times. It counts as working after ${v.target.worked}.`;
};

interface Row {
  skill: PersonalSkill;
  vetting: VettingStatus | null;
  graduation: Graduation | null;
}

export const MySkillsSection: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [builder, setBuilder] = useState<{ skill: PersonalSkill | null } | null>(null);
  const [running, setRunning] = useState<PersonalSkill | null>(null);

  const load = useCallback(async () => {
    try {
      const [skillRes, gradRes] = await Promise.all([
        aaiApi.getMyWorkflowSkills(),
        aaiApi.getMyGraduations().catch(() => ({ data: { graduations: [], sharing_unlocked: false } })),
      ]);
      const grads: Record<string, Graduation> = {};
      (gradRes.data.graduations || []).forEach((g) => { if (!grads[g.skill_id]) grads[g.skill_id] = g; });
      setUnlocked(Boolean(gradRes.data.sharing_unlocked));
      const skills = skillRes.data.skills || [];
      const built = await Promise.all(skills.map(async (skill) => {
        let vetting: VettingStatus | null = null;
        try {
          vetting = (await aaiApi.getSkillVetting(skill.id)).data.vetting;
        } catch {
          vetting = null;
        }
        return { skill, vetting, graduation: grads[skill.id] || null };
      }));
      setRows(built);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const share = async (row: Row) => {
    setBusy(row.skill.id);
    try {
      await aaiApi.proposeGraduation(row.skill.id);
      await load();
      toast.success('Offered. The program team reviews it and your colleagues hear if it is shared.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Could not offer this skill.');
    } finally {
      setBusy(null);
    }
  };

  const build = () => setBuilder({ skill: null });
  const revise = (row: Row) => setBuilder({ skill: row.skill });
  const runSkill = (row: Row) => setRunning(row.skill);
  const logRun = async (skill: PersonalSkill, outcome: RunOutcome, note: string) => {
    await aaiApi.logSkillRun(skill.id, outcome, note || undefined);
    await load();
  };

  const rightFor = (row: Row): React.ReactNode => {
    const grad = row.graduation;
    if (grad && grad.status !== 'declined') {
      return <Action size="sm" variant="ghost" onClick={() => revise(row)}>Open</Action>;
    }
    const ready = Boolean(row.vetting?.vetted);
    if (!ready) {
      return <Action size="sm" variant="quiet" onClick={() => runSkill(row)}>Use and record a run</Action>;
    }
    if (!unlocked) {
      return <Action size="sm" disabled reason="Sharing opens in the Team Adoption module of your training">Share with your team</Action>;
    }
    return (
      <Action size="sm" onClick={() => share(row)} disabled={busy === row.skill.id} reason="Offering">
        Share with your team
      </Action>
    );
  };

  const badgeFor = (row: Row): React.ReactNode => {
    const grad = row.graduation;
    if (grad) {
      const look = SHARE_STATUS[grad.status];
      return <Chip tone={look.tone} dot>{look.label}</Chip>;
    }
    if (row.vetting?.vetted) return <Chip tone="ok" dot>Ready to share</Chip>;
    return <Chip tone="private" dot>Private to you</Chip>;
  };

  return (
    <>
      <Panel className={className}>
        <PanelHead
          kicker="Your own skills"
          title="Skills you built"
          desc="Written down by you, run on your own work, and private until you offer them. A skill counts as working once it has worked three times, and only then is sharing yours to offer."
          action={<Action size="sm" variant="quiet" icon={<Plus size={14} />} onClick={build}>Build a skill</Action>}
        />
        {rows === null && <div className="px-6 py-6"><Loading label="Loading your skills" /></div>}
        {rows !== null && rows.length === 0 && (
          <EmptyState
            title="You have not built a skill yet"
            reason="A skill is your own way of doing something, written down once so you can run it the same way every time. Build one and it stays private to you."
            action={<Action size="sm" onClick={build}>Build a skill</Action>}
          />
        )}
        {rows !== null && rows.length > 0 && (
          <Divided>
            {rows.map((row) => (
              <ListRow
                key={row.skill.id}
                mark={<RouteMark kind="quick_win" size="lg" />}
                title={row.skill.name}
                badge={badgeFor(row)}
                desc={row.skill.problem || undefined}
                foot={progressLine(row.vetting, row.graduation)}
                meta={[
                  { label: 'Runs', value: `${row.vetting?.worked ?? 0} of ${row.vetting?.runs ?? 0} worked` },
                  { label: 'Offered', value: row.graduation ? relativeTime(row.graduation.created_at) : 'Not offered yet' },
                ]}
                right={rightFor(row)}
              />
            ))}
          </Divided>
        )}
      </Panel>

      {builder && (
        <SkillBuilderDrawer
          mode="workflow"
          skill={builder.skill}
          onClose={() => { setBuilder(null); void load(); }}
          onSaved={() => { void load(); }}
        />
      )}

      {running && (
        <Drawer
          onClose={() => { setRunning(null); void load(); }}
          kicker={<Kicker>Your own skills</Kicker>}
          title={running.name}
          sub="Run this skill on real work and record how it went."
          footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={() => setRunning(null)}>Close</Action></div>}
        >
          <SkillRunBody skillId={running.id} onLogRun={(outcome, note) => logRun(running, outcome, note)} />
        </Drawer>
      )}
    </>
  );
};

export default MySkillsSection;
